import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  LayoutDashboard, Calendar, ShoppingCart, CreditCard, 
  MapPin, Package, Users, LogOut, Menu, X, ChevronRight,
  TrendingUp, IndianRupee, Flame, Tag, Heart, HelpCircle, MessageSquare, Gift
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLayout = ({ children }) => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== 'admin')) {
      toast.error('Admin access required');
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    if (user?.role === 'admin') {
      fetchStats();
    }
  }, [user]);

  const menuItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
    { name: 'Bookings', icon: Calendar, path: '/admin/bookings' },
    { name: 'Orders', icon: ShoppingCart, path: '/admin/orders' },
    { name: 'Payments', icon: CreditCard, path: '/admin/payments' },
    { name: 'Temples', icon: MapPin, path: '/admin/temples' },
    { name: 'Pujas', icon: Flame, path: '/admin/pujas' },
    { name: 'Bhets & Daan', icon: Gift, path: '/admin/bhets' },
    { name: 'Products', icon: Package, path: '/admin/products' },
    { name: 'Categories', icon: Tag, path: '/admin/categories' },
    { name: 'FAQs', icon: HelpCircle, path: '/admin/faqs' },
    { name: 'Testimonials', icon: MessageSquare, path: '/admin/testimonials' },
    { name: 'Coupons', icon: Tag, path: '/admin/coupons' },
    { name: 'Users', icon: Users, path: '/admin/users' },
    { name: 'Offline Puja', icon: Heart, path: '/admin/offline-interests' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F4F1ED] flex font-body selection:bg-[#FF7B00] selection:text-white">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-[#1A1A1A]/[0.08] shadow-[4px_0_24px_rgba(0,0,0,0.02)] z-20 flex flex-col transition-all duration-400 ease-[cubic-bezier(0.16,1,0.3,1)]`}>
        {/* Logo */}
        <div className="h-20 px-6 border-b border-[#1A1A1A]/[0.04] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF7B00] to-[#FF9E42] flex items-center justify-center shadow-[0_4px_12px_rgba(255,123,0,0.2)] group-hover:shadow-[0_6px_16px_rgba(255,123,0,0.3)] transition-all">
              <span className="text-white font-heading font-bold text-xl leading-none">प</span>
            </div>
            {sidebarOpen && (
              <span className="font-heading font-bold text-lg text-gray-900 overflow-hidden whitespace-nowrap">Admin Panel</span>
            )}
          </Link>
        </div>

        {/* Menu */}
        <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto scrollbar-hide">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/admin' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 group ${
                  isActive 
                    ? 'bg-gradient-to-r from-[#FF7B00]/10 to-[#FF7B00]/5 text-[#FF7B00] font-semibold font-medium border border-[#FF7B00]/20' 
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 border border-transparent'
                }`}
                title={!sidebarOpen ? item.name : ""}
              >
                <item.icon className={`w-5 h-5 flex-shrink-0 transition-colors ${isActive ? 'text-[#FF7B00]' : 'text-gray-400 group-hover:text-gray-700'}`} />
                {sidebarOpen && <span className="whitespace-nowrap">{item.name}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Toggle & Logout */}
        <div className="p-4 border-t border-[#1A1A1A]/[0.04] flex flex-col gap-2">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex items-center justify-center p-2 text-gray-400 hover:bg-gray-50 hover:text-gray-900 rounded-xl transition-colors border border-transparent hover:border-gray-100"
          >
            {sidebarOpen ? <ChevronRight className="w-5 h-5 rotate-180 transition-transform" /> : <ChevronRight className="w-5 h-5 transition-transform" />}
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-3 w-full text-red-500 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all border border-transparent hover:border-red-100 group"
          >
            <LogOut className="w-5 h-5 flex-shrink-0 group-hover:scale-110 transition-transform" />
            {sidebarOpen && <span className="font-medium whitespace-nowrap">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main data-page-scroll className="flex-1 overflow-auto flex flex-col relative w-full">
        {/* Top Header */}
        <header className="sticky top-0 z-10 nav-glass px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-heading text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 tracking-tight">
              {menuItems.find(item => 
                location.pathname === item.path || 
                (item.path !== '/admin' && location.pathname.startsWith(item.path))
              )?.name || 'Dashboard'}
            </h1>
          </div>
          
          <div className="flex items-center gap-5">
            <div className="text-right hidden sm:block">
              <p className="font-semibold text-gray-900 text-sm">{user?.name}</p>
              <p className="text-xs text-gray-500 font-medium tracking-wide">{user?.email}</p>
            </div>
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FF7B00] to-[#FF9E42] flex items-center justify-center text-white font-bold shadow-[0_4px_12px_rgba(255,123,0,0.2)] border-2 border-white cursor-pointer hover:shadow-[0_4px_16px_rgba(255,123,0,0.3)] transition-shadow">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-8 max-w-7xl mx-auto w-full animate-slide-up">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
